import React from 'react'

import { useDispatch } from 'react-redux'

import {
  HowGoustoFoodBoxesWorkPage,
  HowGoustoFoodBoxesWorkCmsPage,
  MealPlanningPage,
  MealPlanningCmsPage,
  DynamicCmsPage,
} from '@features/marketing-pages'
import {
  fetchCmsBlocks,
  FOOD_BOX_PAGE,
  HOW_GOUSTO_WORKS_PAGE,
  useIsHowGoustoWorksCmsEnabled,
  MEAL_PLANNING_PAGE,
  useIsMealPlanningCmsEnabled,
} from '@library/cms-blocks'
import { fetchByUrl } from '@library/contentstack-client'

import { applyPromoCode } from 'actions/home'
import { redirect } from 'actions/redirect'
import { fetchCmsEntry } from 'apis/cms'
import { ErrorPage } from 'components/ErrorPage/ErrorPage'
import config from 'config/routes'

const CMS_PAGE_CONTENT_TYPE = 'cms_page'

type CmsFetchStore = {
  dispatch: (action: unknown) => unknown
  getState: () => {
    cmsBlocks?: Record<string, { error?: boolean; errorCode?: string } | undefined>
  }
}

// Promo CTAs apply the promo code, then redirect. Shared by every page on these routes.
function usePromocodeRedirect() {
  const dispatch = useDispatch()

  return async (href: string) => {
    await dispatch(applyPromoCode())

    dispatch(redirect(href))
  }
}

// SSR: pre-populate Redux with the CMS entry for `pageUrl` so the page renders without a
// client fetch on first paint. The flag-gated pages always prime regardless of flag state,
// to avoid a content flash when the flag flips on.
const primeCmsPage = (store: CmsFetchStore, pageUrl: string) =>
  store.dispatch(fetchCmsBlocks([{ contentType: CMS_PAGE_CONTENT_TYPE, pageUrl }], fetchByUrl))

// A genuine miss (NOT_FOUND) throws a 404 so the server returns a
// real HTTP 404 — better for SEO. The throw routes through `renderErrorPages`, which clears the
// persistent store (basket); we accept that for now because a genuine miss shouldn't happen on a
// live page and a real 404 matters more. The proper, basket-preserving fix is to set the status
// from store state in `processRequest` without throwing — see the cms-blocks doc, section 5.
// Transient CMS errors do NOT throw: DynamicCmsPage renders the 500 page in-component (HTTP 200,
// basket intact).
function throwIfCmsNotFound(store: CmsFetchStore, pageUrl: string) {
  const entry = store.getState().cmsBlocks?.[`${CMS_PAGE_CONTENT_TYPE}:${pageUrl}`]
  if (entry?.error && entry.errorCode === 'NOT_FOUND') {
    const notFound: Error & { status?: number } = new Error(`No CMS page published at ${pageUrl}`)
    notFound.status = 404
    throw notFound
  }
}

// SSR: prime the entry, then 404 on a genuine miss. Used by every route that renders a
// DynamicCmsPage with no static fallback (a data-driven page is its CMS entry).
async function primeCmsPageOr404(store: CmsFetchStore, pageUrl: string) {
  await primeCmsPage(store, pageUrl)
  throwIfCmsNotFound(store, pageUrl)
}

// The single generic CMS page, wired with the app's fetcher, promo handler, and error page.
function CmsPage({ pageUrl }: { pageUrl: string }) {
  const onTriggerPromocode = usePromocodeRedirect()

  return (
    <DynamicCmsPage
      pageUrl={pageUrl}
      cmsFetcher={fetchCmsEntry}
      onTriggerPromocode={onTriggerPromocode}
      renderError={(status) => <ErrorPage status={status} />}
    />
  )
}

export function HowGoustoWorks() {
  const isHowGoustoWorksCmsEnabled = useIsHowGoustoWorksCmsEnabled()
  const startOnboarding = usePromocodeRedirect()

  // Control renders static; the Optimizely treatment renders the CMS page.
  return isHowGoustoWorksCmsEnabled ? (
    <HowGoustoFoodBoxesWorkCmsPage startOnboarding={startOnboarding} cmsFetcher={fetchCmsEntry} />
  ) : (
    <HowGoustoFoodBoxesWorkPage startOnboarding={startOnboarding} />
  )
}

HowGoustoWorks.fetchData = ({ store }: { store: CmsFetchStore }) =>
  primeCmsPage(store, HOW_GOUSTO_WORKS_PAGE.pageUrl)

export function MealPlanning() {
  const isMealPlanningCmsEnabled = useIsMealPlanningCmsEnabled()
  const startOnboarding = usePromocodeRedirect()

  // Control renders static; the Optimizely treatment renders the CMS page.
  return isMealPlanningCmsEnabled ? (
    <MealPlanningCmsPage startOnboarding={startOnboarding} cmsFetcher={fetchCmsEntry} />
  ) : (
    <MealPlanningPage startOnboarding={startOnboarding} />
  )
}

MealPlanning.fetchData = ({ store }: { store: CmsFetchStore }) =>
  primeCmsPage(store, MEAL_PLANNING_PAGE.pageUrl)

// Route is `/food-box/*`, so the splat is everything after `/food-box/`.
function toFoodBoxPageUrl(splat: string | undefined): string {
  return `/food-box/${splat ?? ''}`.replace(/\/$/, '')
}

type RouteComponentProps = {
  location?: { pathname?: string }
  params?: { splat?: string }
}

export function FoodBoxCmsCatchAll({ location, params }: RouteComponentProps) {
  // Client uses the live pathname; server uses the splat. Same Redux key.
  const pageUrl = location?.pathname ?? toFoodBoxPageUrl(params?.splat)

  return <CmsPage pageUrl={pageUrl} />
}

FoodBoxCmsCatchAll.fetchData = ({
  params,
  store,
}: {
  params?: { splat?: string }
  store: CmsFetchStore
}) => primeCmsPageOr404(store, toFoodBoxPageUrl(params?.splat))

// Each fixed-URL page is the generic CmsPage bound to one CMS URL. The factory exists so every
// route gets its own static `fetchData` — SSR needs to know which entry to prime, and an explicit
// path has no splat to derive it from. (FoodBoxCmsCatchAll can't use this: its URL is per-request.)
function createCmsLandingPage(pageUrl: string) {
  function LandingPage() {
    return <CmsPage pageUrl={pageUrl} />
  }

  LandingPage.fetchData = ({ store }: { store: CmsFetchStore }) => primeCmsPageOr404(store, pageUrl)

  return LandingPage
}

export const FoodBox = createCmsLandingPage(FOOD_BOX_PAGE.pageUrl)
export const HeatAndEat = createCmsLandingPage(config.client.landingHeatAndEat)
export const ScratchHeatAndEat = createCmsLandingPage(config.client.landingScratchHeatAndEat)
